import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'

import { t } from '../i18n'
import { useSession } from './session'

/**
 * The frame around the screens that administer more than one job: access,
 * the access-request queue and the projects of the address.
 *
 * They stay outside the job's shell for the reasons App gives, but they no
 * longer float on a bare page: the same dark bar runs across the top, and a
 * rail down the right lists the administration screens beside one another, so
 * moving from who-can-do-what to the request queue is one click rather than a
 * trip back through a job. The rail is listed for everybody; each screen
 * still decides who may use it, the way the job's own menu does.
 *
 * Administration, the page listing who runs the site, is listed for the
 * Global Admin and the Super Admins, who are the people it is for and
 * the people who land on it after signing in.
 *
 * The bar carries the way back to My Projects, so no administration screen
 * is left without one.
 *
 * The other exception is Integrations, which is listed for the Global Admin only:
 * nobody else can open it, and a service key is not something the rest of the
 * administrators need to know there is a page for. Sign-in is listed the same
 * way and for the same reason. The server refuses anybody else whatever the
 * rail shows.
 *
 * Wall screens is listed for the Global Admin and the Super Admins, the
 * people who choose what every project's wall shows; the server refuses
 * anybody else.
 */
export function AdminFrame({ children }: { children: ReactNode }) {
  const { me } = useSession()
  const role = me.data?.person.platform_role ?? null
  const globalAdmin = role === 'global_admin'
  return (
    <div className="admin-shell">
      <div className="admin-bar">
        <span className="brand-mark" aria-hidden="true">
          SF
        </span>
        <span className="admin-bar-title">{t('app.name')}</span>
        <span className="admin-bar-caption">{t('admin.caption')}</span>
        {/* The way back to the jobs, on the bar every administration screen
            sits under, so none of them is left without one. Sign-out stays
            with the screens that already carry it. */}
        <Link className="admin-bar-back" to="/">
          {t('administration.to_projects')}
        </Link>
      </div>
      <div className="admin-body">
        <div className="admin-page">{children}</div>
        <nav className="admin-rail" aria-label={t('admin.rail')}>
          <h2>{t('admin.rail')}</h2>
          {role !== null ? (
            <NavLink to="/admin" end>
              {t('admin.rail.administration')}
            </NavLink>
          ) : null}
          <NavLink to="/admin/access">{t('admin.rail.access')}</NavLink>
          <NavLink to="/admin/templates">{t('admin.rail.templates')}</NavLink>
          <NavLink to="/admin/access-requests">{t('admin.rail.requests')}</NavLink>
          <NavLink to="/admin/projects">{t('admin.rail.projects')}</NavLink>
          <NavLink to="/admin/site-computers">{t('admin.rail.site_computers')}</NavLink>
          {role === 'super_admin' || globalAdmin ? (
            <NavLink to="/admin/points">{t('admin.rail.points')}</NavLink>
          ) : null}
          {role === 'global_admin' || role === 'super_admin' ? (
            <NavLink to="/admin/wall-screens">{t('admin.rail.wall_screens')}</NavLink>
          ) : null}
          {globalAdmin ? (
            <NavLink to="/admin/integrations">{t('admin.rail.integrations')}</NavLink>
          ) : null}
          {globalAdmin ? (
            <NavLink to="/admin/signin">{t('admin.rail.signin_policy')}</NavLink>
          ) : null}
        </nav>
      </div>
    </div>
  )
}
